import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import type { DynamicEntityRow } from '../../../../api/strapiDynamicEntity';
import { strapiFetchParameterUjiGridByBakuMutu } from '../../../../api/strapiCrm';
import {
  BakuMutuMasterAutocomplete,
  bakuMutuMasterDisplayName,
} from '../../../../components/BakuMutuMasterAutocomplete';
import { JenisPengukuranMasterAutocomplete, jenisPengukuranMasterOptionLabel } from '../../../../components/JenisPengukuranMasterAutocomplete';
import { ParameterUjiByBakuMutuGrid } from '../../../../components/ParameterUjiByBakuMutuGrid';
import { useAuth } from '../../../../auth/AuthContext';
import { queryKeys } from '../../../../query/queryKeys';
import { formatParameterSelectionSummaryFromGridRows } from '../../../../utils/sampleParameterDisplay';
import { useQuery } from '@tanstack/react-query';
import type { QuotationFormSampleRow } from '../quotationFormTypes';

export function emptyQuotationFormSampleRow(): QuotationFormSampleRow {
  return {
    key: `s-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    jenisPengukuranMasterId: '',
    jenisPengukuranLabel: '',
    parameterUji: '',
    bakuMutuMasterId: '',
    bakuMutuLabel: '',
    jumlahTitik: '1',
    harga: '',
  };
}

type Props = {
  open: boolean;
  onClose: () => void;
  /** Baris yang diedit; `null` = tambah baru. */
  initialRow: QuotationFormSampleRow | null;
  onSave: (row: QuotationFormSampleRow) => void;
};

function validateFields(
  jenisMaster: DynamicEntityRow | null,
  bakuMaster: DynamicEntityRow | null,
  parameterUji: string,
  jumlahTitik: string,
  harga: string,
): { ok: true } | { ok: false; message: string } {
  if (!jenisMaster?.documentId?.trim()) {
    return { ok: false, message: 'Pilih jenis pengukuran dari master.' };
  }
  if (!bakuMaster?.documentId?.trim()) {
    return { ok: false, message: 'Pilih baku mutu dari master.' };
  }
  const p = parameterUji.trim();
  if (!p) {
    return { ok: false, message: 'Pilih minimal satu parameter uji (centang baris pada tabel).' };
  }
  const jt = Number.parseInt(jumlahTitik.trim(), 10);
  if (!Number.isFinite(jt) || jt < 1) {
    return { ok: false, message: 'Jumlah titik minimal 1.' };
  }
  const hTrim = harga.trim();
  if (hTrim !== '') {
    const hNum = Number.parseFloat(hTrim.replace(',', '.'));
    if (!Number.isFinite(hNum) || hNum < 0) {
      return { ok: false, message: 'Harga harus angka ≥ 0 atau dikosongkan.' };
    }
  }
  return { ok: true };
}

function masterJenisRowFromFormRow(row: QuotationFormSampleRow | null): DynamicEntityRow | null {
  if (!row?.jenisPengukuranMasterId?.trim()) return null;
  const id = row.jenisPengukuranMasterId.trim();
  const lab = (row.jenisPengukuranLabel ?? '').trim();
  if (lab.includes('—')) {
    const parts = lab.split(/\s*—\s*/).map((s) => s.trim());
    const nama = parts[0] ?? '';
    const kode = parts[1] ?? '';
    return { documentId: id, nama_jenis: nama, kode_jenis: kode };
  }
  return { documentId: id, nama_jenis: lab || undefined };
}

function masterBakuRowFromFormRow(row: QuotationFormSampleRow | null): DynamicEntityRow | null {
  if (!row?.bakuMutuMasterId?.trim()) return null;
  const id = row.bakuMutuMasterId.trim();
  const lab = (row.bakuMutuLabel ?? '').trim();
  if (!lab) return { documentId: id };
  const parts = lab.split(/\s*—\s*/).map((s) => s.trim());
  if (parts.length >= 3) {
    return { documentId: id, kode: parts[0], judul: parts[1], nama_baku_mutu: parts[2] };
  }
  if (parts.length === 2) {
    return { documentId: id, judul: parts[0], nama_baku_mutu: parts[1] };
  }
  return { documentId: id, judul: lab };
}

/** Modal tambah / ubah satu baris sampel quotation. */
export function QuotationSampleFormDialog({
  open,
  onClose,
  initialRow,
  onSave,
}: Props) {
  const { jwt } = useAuth();
  const [jenisMaster, setJenisMaster] = useState<DynamicEntityRow | null>(null);
  const [bakuMaster, setBakuMaster] = useState<DynamicEntityRow | null>(null);
  const [parameterUji, setParameterUji] = useState('');
  const [jumlahTitik, setJumlahTitik] = useState('1');
  const [harga, setHarga] = useState('');
  const [error, setError] = useState<string | null>(null);

  const bmid = bakuMaster?.documentId?.trim();
  const gridRowsQ = useQuery({
    queryKey: queryKeys.parameterUjiGridByBakuMutu(jwt, bmid || null),
    queryFn: () => strapiFetchParameterUjiGridByBakuMutu(jwt!, bmid!),
    enabled: Boolean(open && jwt && Boolean(bmid)),
  });

  const parameterRingkas = useMemo(
    () => formatParameterSelectionSummaryFromGridRows(gridRowsQ.data ?? [], parameterUji),
    [gridRowsQ.data, parameterUji],
  );

  const initialJenis = useMemo(() => masterJenisRowFromFormRow(initialRow), [initialRow]);
  const initialBaku = useMemo(() => masterBakuRowFromFormRow(initialRow), [initialRow]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (initialRow) {
      setJenisMaster(initialJenis);
      setBakuMaster(initialBaku);
      setParameterUji(initialRow.parameterUji);
      setJumlahTitik(initialRow.jumlahTitik.trim() || '1');
      setHarga(initialRow.harga);
    } else {
      setJenisMaster(null);
      setBakuMaster(null);
      setParameterUji('');
      setJumlahTitik('1');
      setHarga('');
    }
  }, [open, initialRow, initialJenis, initialBaku]);

  function handleSubmit(): void {
    const v = validateFields(jenisMaster, bakuMaster, parameterUji, jumlahTitik, harga);
    if (!v.ok) {
      setError(v.message);
      return;
    }
    const hTrim = harga.trim();
    const hNum = hTrim === '' ? null : Number.parseFloat(hTrim.replace(',', '.'));
    const base = initialRow ?? emptyQuotationFormSampleRow();
    const jpId = jenisMaster!.documentId.trim();
    const jpLabel = jenisPengukuranMasterOptionLabel(jenisMaster);
    const bmId = bakuMaster!.documentId.trim();
    const bmLabel = bakuMutuMasterDisplayName(bakuMaster);
    onSave({
      ...base,
      jenisPengukuranMasterId: jpId,
      jenisPengukuranLabel: jpLabel,
      bakuMutuMasterId: bmId,
      bakuMutuLabel: bmLabel,
      parameterUji: parameterUji.trim(),
      jumlahTitik: jumlahTitik.trim(),
      harga: hTrim === '' ? '' : String(hNum),
    });
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth data-testid="quotation-form-sample-dialog">
      <DialogTitle>{initialRow ? 'Ubah sampel' : 'Sampel baru'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Jumlah titik"
            type="number"
            value={jumlahTitik}
            onChange={(e) => setJumlahTitik(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ min: 1, step: 1, 'data-testid': 'quotation-form-sample-dialog-jtitik' }}
          />
          <JenisPengukuranMasterAutocomplete
            value={jenisMaster}
            onChange={setJenisMaster}
            required
            inputTestId="quotation-form-sample-dialog-jenis-pengukuran"
          />
          <BakuMutuMasterAutocomplete
            value={bakuMaster}
            onChange={(v) => {
              if (v?.documentId?.trim() !== bakuMaster?.documentId?.trim()) {
                setParameterUji('');
              }
              setBakuMaster(v);
            }}
            required
            inputTestId="quotation-form-sample-dialog-baku-mutu"
          />
          <ParameterUjiByBakuMutuGrid
            jwt={jwt}
            bakuMutuDocumentId={bakuMaster?.documentId}
            value={parameterUji}
            onChange={setParameterUji}
            disabled={!bakuMaster?.documentId?.trim()}
            gridTestId="quotation-form-sample-dialog-param-grid"
            rowCheckboxTestId={(i) => `quotation-form-sample-dialog-param-cb-${i}`}
            selectAllCheckboxTestId="quotation-form-sample-dialog-param-select-all"
          />
          <TextField
            label="Parameter uji"
            value={parameterRingkas}
            fullWidth
            size="small"
            disabled
            helperText="Nama + simbol (notasi kimia). Nilai tersimpan: documentId parameter uji, bisa banyak."
            inputProps={{ 'data-testid': 'quotation-form-sample-dialog-param-summary' }}
          />
          <TextField
            label="Harga (opsional)"
            type="number"
            value={harga}
            onChange={(e) => setHarga(e.target.value)}
            fullWidth
            size="small"
            helperText="Subtotal baris = jumlah titik × harga (harga per titik)."
            inputProps={{ step: 'any', 'data-testid': 'quotation-form-sample-dialog-harga' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Batal
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} data-testid="quotation-form-sample-dialog-save">
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
}
